import { useEffect, useState } from "react";

/**
 * MoneyBurst — ephemeral particle burst that fires once when `trigger`
 * changes to a new truthy value. Renders 12 particles that fly outward
 * and fade over 900ms.
 *
 * Absolutely-positioned; the parent should be `relative` and typically
 * sits behind/above the save button.
 *
 * Props:
 *   trigger — any value; each new distinct value fires a new burst.
 *   tone    — "pos" (green +$) for income/save-goal, "neg" (red -$) for expense.
 *   count   — number of particles, default 12.
 */
export default function MoneyBurst({ trigger, tone = "pos", count = 12 }) {
  const [burstId, setBurstId] = useState(null);

  useEffect(() => {
    if (trigger === undefined || trigger === null || trigger === false) return;
    // Fire a new burst — using a fresh id so React remounts particles
    setBurstId(Date.now());
    // Auto-clear after the animation completes so the DOM stays clean
    const t = setTimeout(() => setBurstId(null), 1000);
    return () => clearTimeout(t);
  }, [trigger]);

  if (burstId === null) return null;

  const symbol = tone === "neg" ? "−$" : "+$";
  const cls = tone === "neg" ? "money-particle money-particle-neg" : "money-particle money-particle-pos";

  // Precompute per-particle direction + delay so the burst feels organic.
  const particles = Array.from({ length: count }, (_, i) => {
    // Even angular spread with a small random jitter
    const baseAngle = (i / count) * Math.PI * 2;
    const jitter = (Math.random() - 0.5) * 0.5;
    const angle = baseAngle + jitter;
    const distance = 45 + Math.random() * 55; // 45–100 px
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance - 10; // bias slightly upward
    const delay = Math.random() * 80;           // stagger start
    const scale = 0.85 + Math.random() * 0.5;
    return { i, tx, ty, delay, scale };
  });

  return (
    <div
      key={burstId}
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none overflow-visible"
      data-testid={`money-burst-${tone}`}
    >
      {particles.map(({ i, tx, ty, delay, scale }) => (
        <span
          key={i}
          className={cls}
          style={{
            "--tx": `${tx}px`,
            "--ty": `${ty}px`,
            animationDelay: `${delay}ms`,
            fontSize: `${13 * scale}px`,
          }}
        >
          {symbol}
        </span>
      ))}
    </div>
  );
}
